import React, { type ComponentType, type PropsWithChildren, Component, memo } from 'react';
import '@atlaskit/css-reset';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import noop from 'lodash/noop';
import type { EntryPointProps } from 'react-relay';
import { parse as fromQueryString, stringify as toQueryString } from 'query-string';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { metrics } from '@atlassian/browser-metrics';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
import { SmartQueryDetectedFlag } from '@atlassian/jira-issue-navigator-flags/src/ui/smart-query-detected-flag/index.tsx';
import type { IssueNavigatorViewId } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import { convertToView } from '@atlassian/jira-issue-navigator/src/common/utils/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	type Location,
	type Match,
	type RouterActionPush as Push,
	type RouterActionReplace as Replace,
	RouterActions,
	RouterSubscriber,
} from '@atlassian/react-resource-router';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { NinErrorBoundary } from '@atlassian/jira-issue-navigator/src/common/ui/error-boundary/index.tsx';
import type { NestedEntryPoints } from '@atlassian/jira-spa-apps-issue-navigator-common';
import {
	useRoutePushOrReplace,
	type PushOrReplace,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/useRoutePushOrReplace.tsx';
import { useSelectedIssueParamRedirect } from '@atlassian/jira-spa-apps-issue-navigator-common/src/useSelectedIssueParamRedirect.tsx';
import { useFilterChangeInteraction } from '@atlassian/jira-spa-apps-issue-navigator-common/src/useFilterChangeInteraction.tsx';
import {
	useQueryParams,
	usePathParams,
	useCurrentRoute,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/router-selectors.tsx';
import App from './ui/index.tsx';

type IssueKey = string;

type GlobalIssueNavigatorSPAWrapperProps = {
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	push?: Push;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	replace?: Replace;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	location?: Location;
	// Remove when cleaning up jira_nin_last_used_jql_tech_changes
	match?: Match | null | undefined;
	preloadedEntryPoints?: NestedEntryPoints;
	pushOrReplace: PushOrReplace;
	selectedIssue: string | undefined;
};

type GlobalIssueNavigatorSPAWrapperState = {
	initialised: boolean;
	issueKey: string;
};

type MutableSearchParams = {
	issueKey: IssueKey;
	filter?: string;
	jql?: string;
	smartQueryDisabled?: string;
	viewActivity?: boolean;
};

type InteractionProperties = {
	filterId?: string;
	view?: string;
};

const BROWSER_METRICS_INTERACTION_APP_KEY = 'jira.global-issue-navigator';

const initInteractions = () => ({
	pagination: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.pagination.start-end.diff`,
		}),
		properties: {},
	},
	refinement: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.refinement.start-end.diff`,
		}),
		properties: {},
	},
	filter: {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.filter.start-end.diff`,
		}),
		properties: {},
	},
	'list-view': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.list-view.start-end.diff`,
		}),
		properties: {},
	},
	'split-view': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.split-view.start-end.diff`,
		}),
		properties: {},
	},
	'column-configuration': {
		metrics: metrics.interaction({
			key: `${BROWSER_METRICS_INTERACTION_APP_KEY}.column-configuration.start-end.diff`,
		}),
		properties: {},
	},
});

const MaybeNinErrorBoundary = componentWithFG(
	'improve_nin_slo_events',
	NinErrorBoundary,
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
);

type Interactions = ReturnType<typeof initInteractions>;
type InteractionKey = keyof Interactions;

// eslint-disable-next-line jira/react/no-class-components
class GlobalIssueNavigatorSPAWrapper extends Component<
	GlobalIssueNavigatorSPAWrapperProps,
	GlobalIssueNavigatorSPAWrapperState
> {
	constructor(props: GlobalIssueNavigatorSPAWrapperProps) {
		super(props);

		const { location } = props;
		this.activeInteractions = [];
		this.interactions = initInteractions();

		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.state = {
				initialised: false,
				issueKey: this.props.selectedIssue ?? '',
			};
			return;
		}
		if (fg('add_nin_press_interactions')) {
			// Ensure filter change event does not fire on initial loads & transitions because it
			// will cause the page load interaction to be aborted
			const { filter } = this.getQueryStringParams();
			this.lastFilter = filter;
		}

		const query = fromQueryString(location?.search ?? '');
		const issueKey = query.selectedIssue ?? '';

		this.state = {
			initialised: false,
			issueKey,
		};
	}

	shouldComponentUpdate(
		nextProps: Readonly<GlobalIssueNavigatorSPAWrapperProps>,
		nextState: Readonly<GlobalIssueNavigatorSPAWrapperState>,
	): boolean {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			return !(isShallowEqual(this.props, nextProps) && isShallowEqual(this.state, nextState));
		}
		return true;
	}

	componentDidUpdate(prevProps: GlobalIssueNavigatorSPAWrapperProps) {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			const prevIssueKey = prevProps.selectedIssue ?? '';
			const issueKey = this.props.selectedIssue ?? '';

			// If issue key has changed from the router and does not match our internal key then update our state
			if (issueKey !== prevIssueKey && issueKey !== this.state.issueKey) {
				this.setState({
					issueKey,
				});
			}
			return;
		}

		const prevQuery = fromQueryString(prevProps.location?.search ?? '');
		const query = fromQueryString(this.props.location?.search ?? '');

		const prevIssueKey = prevQuery.selectedIssue ?? '';
		const issueKey = query.selectedIssue ?? '';

		// If issue key has changed from the router and does not match our internal key then update our state
		if (issueKey !== prevIssueKey && issueKey !== this.state.issueKey) {
			this.setState({
				issueKey,
			});
		}
	}

	interactions: Interactions;

	activeInteractions: InteractionKey[];

	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	view: IssueNavigatorViewId | undefined = undefined;

	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	lastFilter: string | undefined;

	/**
	 * Update the currently selected issue in the URL.
	 *
	 * @param issueKey Issue key to update in the URL
	 * @param isSelectedByUserInteraction True if this issue was selected by a user interaction, e.g. clicking an issue
	 * in the list. This will be false if an issue was automatically selected by the app, e.g. automatically selecting
	 * the first issue once the issue list has loaded.
	 * @param viewActivity True to indicate the issue app should scroll to the activity feed section after opening.
	 */
	onChangeIssue = (
		issueKey: IssueKey,
		isSelectedByUserInteraction: boolean,
		viewActivity?: boolean,
	) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: {
						selectedIssue: issueKey === '' ? undefined : issueKey,
						viewActivity: viewActivity?.toString(),
					},
				},
				// Replace the history entry when an issue is auto selected by the app so we're not pushing redundant entries to
				// the history stack
				!isSelectedByUserInteraction,
			);
			return;
		}

		// Replace the history entry when an issue is auto selected by the app so we're not pushing redundant entries to
		// the history stack
		this.push(
			{
				issueKey,
				viewActivity,
			},
			!isSelectedByUserInteraction,
		);
	};

	onSetView = (newView: IssueNavigatorViewId, issueKey: IssueKey) => {
		this.startInteraction(convertToView(newView));
		fg('add_nin_press_interactions') && traceUFOPress(`issue-navigator.${convertToView(newView)}`);

		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: {
						selectedIssue: issueKey === '' ? undefined : issueKey,
						...(newView === 'detail' ? { groupBy: undefined, hideDone: undefined } : {}),
					},
				},
				true,
			);
			return;
		}

		this.view = newView;
		if (issueKey !== undefined) {
			this.push({ issueKey }, true);
		}
	};

	onRefinement = () => {
		this.startInteraction('refinement');
		fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.refinement');
	};

	/**
	 * Callback function called when internal changes are made to PIN's state which generates new JQL.
	 *
	 * @param jql string The new JQL string
	 * @param clearFilter boolean When true we clear the filter from url params
	 */
	onChangeJql = (jql: string, clearFilter = false) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace({
				query: {
					// Clear issue key from URL whenever JQL changes
					selectedIssue: undefined,
					...(clearFilter ? { filter: undefined } : {}),
					jql,
				},
			});
			return;
		}

		this.push({
			...(clearFilter ? { filter: undefined } : {}),
			jql,
			// Clear issue key from URL whenever JQL changes
			issueKey: '',
		});
	};

	onPageDataLoad = (selectedView: IssueNavigatorViewId) => {
		if (!fg('jira_nin_last_used_jql_tech_changes')) {
			if (selectedView !== undefined) {
				this.view = selectedView;
			}
			if (!this.state.initialised) {
				this.setState({
					initialised: true,
				});
			}
		}

		this.endInteractions();
	};

	onStartFilterChangeInteractionIfAny = (filter?: string) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `onStartFilterChangeInteractionIfAny` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		if (this.lastFilter !== filter) {
			this.lastFilter = filter;

			if (filter != null && isFilterId(filter)) {
				this.startInteraction('filter', { filterId: filter });
				fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.filter');
			}
		}
	};

	onChangeColumnConfiguration = () => {
		this.startInteraction('column-configuration');
		fg('add_nin_press_interactions') && traceUFOPress('issue-navigator.column-configuration');
	};

	onChangeFilter = (filter?: string) => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			this.pushOrReplace(
				{
					query: { ...(filter ? { filter, jql: undefined } : { jql: undefined }) },
				},
				true,
			);
			return;
		}

		this.push({ ...(filter ? { filter, jql: undefined } : { jql: undefined }) }, true);
	};

	/** Dispatch route history action with provided attributes and set selected issue into state (if modified). */
	pushOrReplace: PushOrReplace = (attributes, shouldReplace) => {
		this.props.pushOrReplace(attributes, shouldReplace);
		if (attributes.query && 'selectedIssue' in attributes.query) {
			// We explicitly set this into state instead of being purely prop driven as browser history actions are not
			// re-rendered synchronously. Setting into state makes our render order more reliable.
			this.setState({
				issueKey: attributes.query.selectedIssue ?? '',
			});
		}
	};

	push(newParams: Partial<MutableSearchParams>, shouldReplace = false) {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `push` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		const { push, location, replace } = this.props;
		const historyAction = shouldReplace ? replace : push;

		const oldIssueKey = this.state.issueKey;

		const { issueKey = oldIssueKey ?? '', ...params } = newParams;

		const issueKeyChanged = newParams.issueKey !== oldIssueKey;
		// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
		const query = fromQueryString(location?.search ?? '', { arrayFormat: 'comma' });

		const queryParamsChanged =
			Object.keys(params).length !== 0 &&
			!isEqual(
				pickBy(params, (param) => param),
				query,
			);

		if (issueKeyChanged || queryParamsChanged) {
			const queryParams = {
				...query,
				...params,
				selectedIssue: issueKey,
			};

			if (issueKey === '') {
				delete queryParams.selectedIssue;
			}

			// @ts-expect-error - React-resource-router's external type interface claims push/replace only accept "string", however the underlying code works with the `Location` object we're using
			historyAction({
				pathname: '/issues/',
				// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
				search: toQueryString(queryParams, { arrayFormat: 'comma' }),
			});
		}

		if (issueKeyChanged) {
			// We explicitly set this into state instead of being purely prop driven as browser history actions are not
			// re-rendered synchronously. Setting into state makes our render order more reliable.
			this.setState({
				issueKey: issueKey ?? '',
			});
		}

		return true;
	}

	startInteraction = (key: InteractionKey, properties?: InteractionProperties) => {
		if (!this.activeInteractions.includes(key)) {
			this.activeInteractions.push(key);
		}
		const interaction = this.interactions[key];

		if (interaction) {
			interaction.properties = properties || {};
			interaction.metrics.start();
		}
	};

	endInteractions = () => {
		this.activeInteractions.forEach((key) => {
			this.interactions[key]?.metrics.stop({
				customData: this.interactions[key]?.properties,
			});
		});
		this.activeInteractions = [];
	};

	getQueryStringParams = () => {
		if (fg('jira_nin_last_used_jql_tech_changes')) {
			throw new Error(
				'Method `getQueryStringParams` deprecated behind jira_nin_last_used_jql_tech_changes and to be removed on cleanup',
			);
		}

		const { location } = this.props;
		const { filter, jql } = fromQueryString(location?.search ?? '', {
			// @ts-expect-error - The "comma" option requires query-string v6.4.0 or newer. JFE currently uses v5.1.1.
			arrayFormat: 'comma',
		});

		return { filter, jql };
	};

	render() {
		let filter;
		let jql;
		if (!fg('jira_nin_last_used_jql_tech_changes')) {
			const params = this.getQueryStringParams();
			filter = params.filter;
			jql = params.jql;

			this.onStartFilterChangeInteractionIfAny(filter);
		}

		return (
			<>
				<MaybeNinErrorBoundary id="unhandled" packageName="jiraSpaAppsGlobalIssueNavigator">
					<App
						issueKey={this.state.issueKey}
						jql={jql}
						onChangeIssue={this.onChangeIssue}
						onPageDataLoad={this.onPageDataLoad}
						onChangeColumnConfiguration={this.onChangeColumnConfiguration}
						onChangeJql={this.onChangeJql}
						onChangeFilter={this.onChangeFilter}
						initialised={this.state.initialised}
						onRefinement={this.onRefinement}
						onSetView={this.onSetView}
						view={
							this.view && !fg('jira_nin_last_used_jql_tech_changes')
								? convertToView(this.view)
								: undefined
						}
						filter={filter}
						preloadedEntryPoints={this.props.preloadedEntryPoints}
					/>
					{this.state.initialised && !fg('jira_nin_last_used_jql_tech_changes') && (
						<JSErrorBoundary
							id="smartQueryDetectedFlag"
							packageName="jiraSpaAppsGlobalIssueNavigator"
							teamName="empanada"
							fallback="unmount"
						>
							<SmartQueryDetectedFlag jql={jql} />
						</JSErrorBoundary>
					)}
				</MaybeNinErrorBoundary>
			</>
		);
	}
}

type EntryPointRuntimeProps = {
	preloadedEntryPoints?: NestedEntryPoints;
};

const GlobalIssueNavigatorSPAWithRouterSubscriber: ComponentType<
	EntryPointProps<{}, {}, EntryPointRuntimeProps, {}>
> = memo(({ props }) => (
	<UFOSegment name="global-issue-navigator">
		<RouterActions>
			{({ push, replace }) => (
				<RouterSubscriber>
					{({ location, match }) => (
						<GlobalIssueNavigatorSPAWrapper
							push={push}
							location={location}
							match={match}
							replace={replace}
							preloadedEntryPoints={props.preloadedEntryPoints}
							pushOrReplace={noop}
							selectedIssue={undefined}
						/>
					)}
				</RouterSubscriber>
			)}
		</RouterActions>
	</UFOSegment>
));

const GlobalIssueNavigatorSPA: ComponentType<
	EntryPointProps<{}, {}, EntryPointRuntimeProps, {}>
> = ({ props }) => {
	const query = useQueryParams();
	const params = usePathParams();
	const route = useCurrentRoute();
	const pushOrReplace = useRoutePushOrReplace(query, params, route);

	// Handle redirecting from /:issueKey path param to ?selectedIssue query param
	useSelectedIssueParamRedirect(params, pushOrReplace);

	useFilterChangeInteraction(query);

	return (
		<UFOSegment name="global-issue-navigator">
			<GlobalIssueNavigatorSPAWrapper
				pushOrReplace={pushOrReplace}
				selectedIssue={query.selectedIssue}
				preloadedEntryPoints={props.preloadedEntryPoints}
			/>
		</UFOSegment>
	);
};

export default componentWithFG(
	'jira_nin_last_used_jql_tech_changes',
	memo(GlobalIssueNavigatorSPA),
	GlobalIssueNavigatorSPAWithRouterSubscriber,
);
